import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageHeader from '../components/ui/PageHeader';
import CTASection from '../components/home/CTASection';
import * as FiIcons from 'react-icons/fi';
import { jpgImages, videos, formatDropboxUrl } from '../data/galleryMedia';

const { FiX, FiPlay, FiInstagram, FiVideo, FiCamera } = FiIcons;

export default function Gallery() {
  const [activeTab, setActiveTab] = useState('photos');
  const [lightboxItem, setLightboxItem] = useState(null);

  const tabs = [
    { id: 'photos', label: 'Recent Work', icon: FiCamera, count: jpgImages.length },
    { id: 'videos', label: 'Video Highlights', icon: FiVideo, count: videos.length }
  ];

  const openLightbox = (item, type) => {
    setLightboxItem({ url: formatDropboxUrl(item), type });
  };

  const closeLightbox = () => setLightboxItem(null);

  return (
    <div className="bg-slate-950 min-h-screen">
      <PageHeader 
        title="Our Work" 
        subtitle="Real vehicles. Real details. Real work from A.R. Elite Detailing." 
      />

      {/* Stats strip */}
      <div className="bg-slate-900 border-b border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-10 md:gap-20">
            {[
              { value: "100%", label: "Satisfaction Focus" },
              { value: "Veteran", label: "Owned & Operated" },
              { value: "Pro", label: "Grade Products" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-black text-blue-500 mb-1">{stat.value}</div>
                <div className="text-slate-400 text-xs uppercase tracking-widest font-bold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-4">
              Real Results
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm leading-relaxed">
              Real vehicles. Real details. Real work from A.R. Elite Detailing. Browse our complete collection of recent details, ceramic coatings, and paint corrections.
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-sm text-xs font-bold uppercase tracking-widest transition-all ${
                    isActive 
                      ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' 
                      : 'bg-slate-900 text-slate-400 border border-slate-800 hover:border-blue-500/50 hover:text-white'
                  }`}
                >
                  <tab.icon className={isActive ? "text-white" : "text-blue-500"} />
                  {tab.label}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-sm text-[10px] ${isActive ? 'bg-blue-700 text-white' : 'bg-slate-800 text-slate-500'}`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Gallery Grids */}
          <div className="min-h-[50vh]">
            <AnimatePresence mode="wait">
              
              {/* PHOTOS TAB */}
              {activeTab === 'photos' && (
                <motion.div
                  key="photos"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
                >
                  {jpgImages.map((url, i) => (
                    <div 
                      key={`jpg-${i}`} 
                      className="relative aspect-square bg-slate-900 border border-slate-800 overflow-hidden group cursor-pointer rounded-sm"
                      onClick={() => openLightbox(url, 'jpg')}
                    >
                      <img 
                        src={formatDropboxUrl(url)} 
                        alt={`A.R. Elite Detailing Photo ${i + 1}`} 
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/10 transition-colors pointer-events-none" />
                    </div>
                  ))}
                </motion.div>
              )}

              {/* VIDEOS TAB */}
              {activeTab === 'videos' && (
                <motion.div
                  key="videos"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"
                >
                  {videos.map((url, i) => (
                    <div 
                      key={i} 
                      className="relative aspect-[4/5] bg-slate-900 border border-slate-800 overflow-hidden group cursor-pointer rounded-sm"
                      onClick={() => openLightbox(url, 'video')}
                    >
                      <video 
                        src={formatDropboxUrl(url)} 
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-500"
                        muted playsInline
                        onMouseEnter={(e) => {
                          if (e.target.ended) e.target.currentTime = 0;
                          e.target.play().catch(() => {});
                        }}
                        onMouseLeave={(e) => e.target.pause()}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-14 h-14 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 group-hover:scale-110 group-hover:bg-blue-600/80 group-hover:border-blue-400 transition-all duration-300">
                          <FiPlay className="text-white text-xl ml-1" />
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}

            </AnimatePresence>
          </div>

          {/* Social Follow Note */}
          <div className="mt-16 bg-slate-900 border border-slate-800 rounded-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-600/10 border border-blue-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                <FiInstagram className="text-blue-500 text-xl" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm uppercase tracking-wide mb-1">Want more daily updates?</h3>
                <p className="text-slate-400 text-xs">Follow our social media channels for the latest transformations.</p>
              </div>
            </div>
            <a 
              href="https://www.instagram.com/a.r.elite_detailing" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-slate-700 text-white font-bold rounded-sm hover:border-blue-500 hover:text-blue-400 transition-all uppercase tracking-wide text-xs whitespace-nowrap w-full sm:w-auto"
            >
              Follow on Instagram
            </a>
          </div>

        </div>
      </div>

      {/* Lightbox Overlay */}
      <AnimatePresence>
        {lightboxItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-slate-950/98 flex items-center justify-center p-4 md:p-8"
            onClick={closeLightbox}
          >
            <button 
              className="absolute top-4 right-4 md:top-6 md:right-6 w-12 h-12 bg-slate-900 border border-slate-700 rounded-full flex items-center justify-center text-white hover:bg-blue-600 hover:border-blue-500 transition-colors z-10"
              onClick={closeLightbox}
            >
              <FiX className="text-xl" />
            </button>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="max-w-5xl w-full max-h-[85vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {lightboxItem.type === 'jpg' ? (
                <img 
                  src={lightboxItem.url} 
                  alt="A.R. Elite Detailing High Res" 
                  className="max-w-full max-h-[85vh] object-contain rounded-sm shadow-2xl border border-slate-800"
                />
              ) : (
                <video 
                  src={lightboxItem.url} 
                  className="max-w-full max-h-[85vh] object-contain rounded-sm shadow-2xl border border-slate-800 bg-black"
                  controls autoPlay playsInline
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CTASection />
    </div>
  );
}